import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Trash2, Package, Recycle } from "lucide-react";
import { Countdown } from "@/components/Kit";

function toPascal(s) { return (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(""); }

export default function DiscardPile() {
  const { refreshUser } = useOutletContext();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/discard-pile"); setD(data); };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/discard-pile/claim");
      toast.success(data.message); refreshUser?.(); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); await load(); } finally { setBusy(false); }
  };

  if (!d) return null;
  return (
    <div data-testid="discard-pile-page" className="max-w-3xl mx-auto">
      <span className="label-cap text-primary flex items-center gap-2"><Recycle className="w-4 h-4" /> The Discard Pile</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1">Salvage Conveyor</h1>
      <p className="text-muted-foreground mb-5">A public belt of parts other pilots have discarded. Grab one free low-tier scrap asset every 5 minutes.</p>

      <div className="panel-glow p-4 overflow-hidden mb-6">
        <div className="label-cap mb-3 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> On the belt right now</div>
        <div className="flex gap-3 overflow-x-auto pb-2 conveyor-track" data-testid="discard-belt">
          {d.belt.length === 0 && <p className="text-sm text-muted-foreground py-4">The belt is empty — check back after pilots toss some scrap.</p>}
          {d.belt.map((it) => {
            const Icon = Icons[toPascal(it.icon)] || Package;
            return (
              <div key={it.scrap_id} className="panel p-3 shrink-0 w-24 flex flex-col items-center gap-1.5 text-center">
                <Icon className="w-8 h-8 text-primary" />
                <span className="text-[10px] font-bold leading-none truncate w-full">{it.name}</span>
                <span className="label-cap text-[8px]">{it.from_name || "unknown"}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-center">
        {d.can_claim ? (
          <button data-testid="discard-claim-btn" onClick={claim} disabled={busy} className="btn-primary text-base px-10 py-4"><Recycle className="w-5 h-5" /> Claim Free Scrap</button>
        ) : (
          <div data-testid="discard-cooldown" className="panel px-6 py-4 inline-block">
            <div className="label-cap">Belt recycling — next grab in</div>
            <Countdown seconds={d.next_claim_in} onDone={load} className="font-head text-2xl font-black text-primary" />
          </div>
        )}
      </div>
    </div>
  );
}
